import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";
import clsx from "clsx";
import { SYMBOLS, TIMEFRAMES, type SymbolEntry } from "@/lib/symbols";
import { searchInstruments, toSymbolEntry, type Instrument } from "@/lib/instruments";

interface PaneHeaderProps {
  symbol: string;
  exchange: string;
  symbolName: string;
  timeframe: string;
  isActive: boolean;
  /** Compact mode is used in multi-chart layouts with 4+ panes */
  compact?: boolean;
  /** Upstream tick-stream connection state (shared socket). */
  streamStatus?: "connecting" | "connected" | "disconnected";
  /** True once this pane's symbol has received at least one tick. */
  hasTicks?: boolean;
  onSymbolChange: (entry: SymbolEntry) => void;
  onTimeframeChange: (tf: string) => void;
}

/**
 * Live indicator — honest by design:
 *   green pulse = connected AND this symbol has ticked (actually live)
 *   amber       = socket reconnecting (was live, temporarily blind)
 *   nothing     = no live stream for this symbol (US/Yahoo etc.)
 */
function LiveDot({ status, hasTicks }: { status?: string; hasTicks?: boolean }) {
  if (status === "connected" && hasTicks) {
    return (
      <span className="relative flex h-1.5 w-1.5 shrink-0" title="Live — receiving ticks">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bull opacity-60" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-bull" />
      </span>
    );
  }
  if (status === "connecting" && hasTicks) {
    return (
      <span
        className="inline-flex h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-warn"
        title="Reconnecting to tick stream"
      />
    );
  }
  return null;
}

export function PaneHeader({
  symbol, exchange, symbolName, timeframe, isActive, compact,
  streamStatus, hasTicks,
  onSymbolChange, onTimeframeChange,
}: PaneHeaderProps) {
  return (
    <div
      className={clsx(
        "flex shrink-0 items-center justify-between border-b border-border bg-bg-panel px-2 py-1",
        isActive && "border-b-accent/40"
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <LiveDot status={streamStatus} hasTicks={hasTicks} />
        <CompactSymbolPicker
          symbol={symbol}
          exchange={exchange}
          symbolName={symbolName}
          onChange={onSymbolChange}
        />
      </div>

      <CompactTimeframePicker
        value={timeframe}
        onChange={onTimeframeChange}
        compact={compact}
      />
    </div>
  );
}

function CompactSymbolPicker({
  symbol, exchange, symbolName, onChange,
}: {
  symbol: string;
  exchange: string;
  symbolName: string;
  onChange: (entry: SymbolEntry) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const q = query.trim().toUpperCase();

  // Server search against the archive. The curated SYMBOLS list is ~21
  // entries and cannot cover option strikes -- thousands of them, changing
  // weekly. Each keystroke aborts the previous request so a slow response
  // cannot land after a newer one.
  const [remote, setRemote] = useState<Instrument[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!q) {
      setRemote([]);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current?.abort();
    abortRef.current = ctrl;
    const t = setTimeout(() => {
      searchInstruments(q, ctrl.signal)
        .then(setRemote)
        // Falls back to the local list. A search outage should narrow what
        // you can find, not break the picker.
        .catch(() => setRemote([]));
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const local = q
    ? SYMBOLS.filter(
        (s) =>
          s.symbol.includes(q) ||
          s.name.toUpperCase().includes(q) ||
          s.exchange.includes(q)
      )
    : SYMBOLS;

  const seen = new Set(remote.map((r) => `${r.exchange}:${r.symbol}`));
  const matches: SymbolEntry[] = q
    ? [
        ...remote.map(toSymbolEntry),
        ...local.filter((s) => !seen.has(`${s.exchange}:${s.symbol}`)),
      ].slice(0, 30)
    : SYMBOLS.slice(0, 50);

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex min-w-0 items-center gap-1.5 rounded px-1.5 py-1 transition-colors hover:bg-bg-elevated"
      >
        <span className="font-mono text-xs font-semibold text-text-primary truncate">{symbol}</span>
        <span className="text-[10px] uppercase tracking-wider text-text-muted shrink-0">
          {exchange}
        </span>
        <ChevronDown className="h-3 w-3 shrink-0 text-text-muted" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded-md border border-border bg-bg-elevated shadow-elevated animate-slide-up">
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-text-muted" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search symbol…"
              className="flex-1 bg-transparent text-xs text-text-primary placeholder:text-text-muted focus:outline-none"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {matches.map((s) => (
              <button
                key={`${s.exchange}:${s.symbol}`}
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                  setQuery("");
                }}
                className={clsx(
                  "flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left transition-colors hover:bg-bg-hover",
                  s.symbol === symbol && s.exchange === exchange && "bg-accent/10"
                )}
              >
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="font-mono text-xs font-semibold text-text-primary shrink-0">{s.symbol}</span>
                  <span className="text-2xs text-text-secondary truncate">{s.name}</span>
                </div>
                <span className="text-2xs uppercase tracking-wider text-text-muted shrink-0">
                  {s.exchange}
                </span>
              </button>
            ))}
            {matches.length === 0 && (
              <div className="px-3 py-2 text-2xs text-text-muted">No match for "{q}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CompactTimeframePicker({
  value, onChange, compact,
}: {
  value: string;
  onChange: (tf: string) => void;
  compact?: boolean;
}) {
  // In compact mode (4+ panes) show fewer TFs inline + a dropdown for the rest
  const visibleTimeframes = compact
    ? TIMEFRAMES.filter((tf) => ["5m", "15m", "1h", "1d"].includes(tf.value))
    : TIMEFRAMES.filter((tf) => ["5m", "15m", "30m", "1h", "4h", "1d", "1w"].includes(tf.value));

  return (
    <div className="flex items-center gap-px rounded border border-border bg-bg-deep p-px shrink-0">
      {visibleTimeframes.map((tf) => (
        <button
          key={tf.value}
          onClick={() => onChange(tf.value)}
          className={clsx(
            "rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold transition-colors",
            value === tf.value
              ? "bg-accent text-white"
              : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
          )}
        >
          {tf.label}
        </button>
      ))}
    </div>
  );
}
